import { axiosInstance } from '@/shared/api/axios';
import {
  CustomerBulkUploadPostRequestType,
  CustomerBulkUploadPostResponseType,
  CustomerDuplicateCheckPostRequestType,
  CustomerDuplicateCheckPostResponseType,
  CustomerDeleteRequestType,
  CustomerDeleteResponseType,
  CustomerGetRequestType,
  CustomerGetTableResultType,
  CustomerPostRequestType,
  CustomerPostResponseType,
  CustomerPutRequestType,
  CustomerPutResponseType,
  CustomerFileDownloadResponseType,
  CustomerFileDownloadPostRequestType,
  CustomBulkSettingGetRequestType,
  CustomBulkSettingGetResponseType,
  CustomerCustomBulkUploadPostRequestType,
  CustomerCustomBulkUploadPostResponseType,
  BulkUpdateEntityTypeResponseType,
  UpdateEntityTypeRequestType,
} from '@/shared/types/api/api.customer';
import { replaceAllSpaceToHalfSpace } from '@/shared/utils/space';

export const searchCustomers = async ({
  groupId,
  folderId,
  pageSize,
  pageIndex,
  sortTarget,
  sortOrder,
  customerName,
  checkedAt,
  isNotExecutedUntilNow,
  managementNumber,
}: CustomerGetRequestType): Promise<CustomerGetTableResultType> => {
  const { data } = await axiosInstance.post<CustomerGetTableResultType>(
    `/group/${groupId}/customer/search`,
    {
      checkedAt,
      isNotExecutedUntilNow,
      managementNumbers: managementNumber
        ? replaceAllSpaceToHalfSpace(managementNumber)
            .split(' ')
            .filter((d) => d !== '')
        : [],
      customerNames: customerName
        ? replaceAllSpaceToHalfSpace(customerName)
            .split(' ')
            .filter((d) => d !== '')
        : [],
    },
    {
      params: {
        folderId,
        limit: pageSize,
        offset: (pageIndex - 1) * pageSize,
        sortTarget,
        sortOrder,
      },
    },
  );
  return data;
};

export const createCustomers = async ({
  groupId,
  customers,
  executionConditionId,
  folderId,
  folderName,
  removeSpaceInCustomerName,
  duplicateCheckInRelativeWord,
}: CustomerPostRequestType): Promise<CustomerPostResponseType> => {
  const { data } = await axiosInstance.post<CustomerPostResponseType>(
    `/group/${groupId}/customer/individual`,
    {
      customers,
      executionConditionId,
      folderId,
      folderName,
      removeSpaceInCustomerName,
      duplicateCheckInRelativeWord,
    },
  );

  return data;
};

export const updateCustomer = async ({
  groupId,
  customerId,
  executionConditionId,
  relativeWord,
  folderId,
  folderName,
  isExecuteWorldCheck,
  entityType,
  spnAgeOrYear,
  worldCheckCaseScreeningItemIdAndValueModelList,
  managementNumber,
  companyNumber,
  address,
  url,
}: CustomerPutRequestType): Promise<CustomerPutResponseType> => {
  const { data } = await axiosInstance.put<CustomerPutResponseType>(
    `/group/${groupId}/customer/${customerId}`,
    {
      executionConditionId,
      relativeWord,
      folderId,
      folderName,
      isExecuteWorldCheck,
      entityType,
      spnAgeOrYear,
      worldCheckCaseScreeningItemIdAndValueModelList,
      managementNumber,
      companyNumber,
      address,
      url,
    },
  );

  return data;
};

export const deleteCustomers = async ({
  groupId,
  customerIds,
}: CustomerDeleteRequestType): Promise<CustomerDeleteResponseType> => {
  const { data } = await axiosInstance.post<CustomerDeleteResponseType>(
    `/group/${groupId}/customer/delete`,
    {
      customerIds,
    },
  );

  return data;
};

export const downloadCustomerFiles = async ({
  groupId,
  customerIds,
  folderId,
}: CustomerFileDownloadPostRequestType): Promise<CustomerFileDownloadResponseType> => {
  const { data } = await axiosInstance.post<CustomerFileDownloadResponseType>(
    `/group/${groupId}/customer/file`,
    {
      customerIds,
      folderId,
    },
  );

  return data;
};

export const bulkUpdateCustomers = async ({
  groupId,
  customers,
  removeSpaceInCustomerName,
  duplicateCheckInRelativeWord,
  fileName,
}: CustomerBulkUploadPostRequestType): Promise<CustomerBulkUploadPostResponseType> => {
  const { data } = await axiosInstance.post<CustomerBulkUploadPostResponseType>(
    `/group/${groupId}/customer/bulk`,
    {
      customers,
      fileName,
      removeSpaceInCustomerName,
      duplicateCheckInRelativeWord,
    },
    {
      timeout: 300000,
    },
  );

  return data;
};

export const customBulkUpdateCustomers = async ({
  groupId,
  customers,
  removeSpaceInCustomerName,
  duplicateCheckInRelativeWord,
}: CustomerCustomBulkUploadPostRequestType): Promise<CustomerCustomBulkUploadPostResponseType> => {
  const { data } = await axiosInstance.post<CustomerCustomBulkUploadPostResponseType>(
    `/group/${groupId}/customer/customBulk`,
    {
      customers,
      removeSpaceInCustomerName,
      duplicateCheckInRelativeWord,
    },
    {
      timeout: 300000,
    },
  );

  return data;
};

export const duplicateCheckCustomers = async ({
  groupId,
  customers,
  removeSpaceInCustomerName,
}: CustomerDuplicateCheckPostRequestType): Promise<CustomerDuplicateCheckPostResponseType> => {
  const { data } = await axiosInstance.post<CustomerDuplicateCheckPostResponseType>(
    `/group/${groupId}/customer/duplicate-check`,
    {
      customers,
      removeSpaceInCustomerName,
    },
    {
      timeout: 300000,
    },
  );

  return data;
};

export const getCustomBulkSetting = async ({
  groupId,
}: CustomBulkSettingGetRequestType): Promise<CustomBulkSettingGetResponseType> => {
  const { data } = await axiosInstance.get<CustomBulkSettingGetResponseType>(
    `/group/${groupId}/customer/customBulkSetting`,
    {},
  );
  return data;
};

export const bulkUpdateEntityType = async (
  groupId: number,
  requestBody: UpdateEntityTypeRequestType,
): Promise<BulkUpdateEntityTypeResponseType> => {
  const { data } = await axiosInstance.put<BulkUpdateEntityTypeResponseType>(
    `/group/${groupId}/entity-type/bulk`,
    requestBody,
  );

  return data;
};

export const bulkUpdateCustomerUrl = async (
  groupId: number,
  requestBody: { updateCustomerUrlModelList: { customerId: number; url: string }[] },
): Promise<BulkUpdateEntityTypeResponseType> => {
  const { data } = await axiosInstance.put<BulkUpdateEntityTypeResponseType>(
    `/group/${groupId}/customer-url/bulk`,
    requestBody,
  );

  return data;
};
